import { useCallback, useEffect, useState } from "react";
import { Address, formatEther, isAddress, parseEther } from "viem";
import { polygon } from "viem/chains";
import { useAccount, usePublicClient, useWalletClient } from "wagmi";
import { KeyRound, PauseCircle, RefreshCw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { INCEPTION_VAULT_ABI } from "@/features/inception/config";

type SessionRow = {
  key: Address;
  controller: Address;
  validAfter: number;
  validUntil: number;
  perCall: bigint;
  daily: bigint;
  canSign: boolean;
  active: boolean;
};

export function SessionKeyPanel({ vault }: { vault: Address }) {
  const { address } = useAccount();
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient({ chainId: polygon.id });
  const { toast } = useToast();
  const [sessionKey, setSessionKey] = useState("");
  const [hours, setHours] = useState("24");
  const [perCall, setPerCall] = useState("0");
  const [daily, setDaily] = useState("0");
  const [canSign, setCanSign] = useState(true);
  const [target, setTarget] = useState("");
  const [selector, setSelector] = useState("");
  const [tokenPerCall, setTokenPerCall] = useState("0");
  const [tokenDaily, setTokenDaily] = useState("0");
  const [allowUnlimited, setAllowUnlimited] = useState(false);
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [paused, setPausedState] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!publicClient) return;
    const [count, isPaused] = await Promise.all([
      publicClient.readContract({ address: vault, abi: INCEPTION_VAULT_ABI, functionName: "sessionKeyCount" }),
      publicClient.readContract({ address: vault, abi: INCEPTION_VAULT_ABI, functionName: "paused" }),
    ]);
    const rows = await Promise.all(Array.from({ length: Number(count) }, async (_, index) => {
      const result = await publicClient.readContract({
        address: vault, abi: INCEPTION_VAULT_ABI, functionName: "sessionKeyAt", args: [BigInt(index)],
      });
      const [key, policy, active] = result as readonly [Address, {
        controller: Address; validAfter: number; validUntil: number; maxNativeValuePerCall: bigint;
        dailyNativeValueLimit: bigint; canSign: boolean;
      }, boolean];
      return { key, controller: policy.controller, validAfter: Number(policy.validAfter), validUntil: Number(policy.validUntil),
        perCall: policy.maxNativeValuePerCall, daily: policy.dailyNativeValueLimit, canSign: policy.canSign, active };
    }));
    setSessions(rows);
    setPausedState(Boolean(isPaused));
  }, [publicClient, vault]);

  useEffect(() => { refresh().catch(() => undefined); }, [refresh]);

  const write = async (functionName: string, args: readonly unknown[]) => {
    if (!address || !walletClient || !publicClient) throw new Error("Connect the Inception Token owner wallet.");
    if ((await walletClient.getChainId()) !== polygon.id) await walletClient.switchChain({ id: polygon.id });
    const simulation = await publicClient.simulateContract({ address: vault, abi: INCEPTION_VAULT_ABI, functionName, args, account: address } as never);
    const hash = await walletClient.writeContract(simulation.request);
    const receipt = await publicClient.waitForTransactionReceipt({ hash });
    if (receipt.status !== "success") throw new Error(`${functionName} reverted.`);
    await refresh();
  };

  const run = async (label: string, operation: () => Promise<void>) => {
    setBusy(true);
    try { await operation(); toast({ title: label }); }
    catch (error) { toast({ title: `${label} failed`, description: error instanceof Error ? error.message : "Transaction failed.", variant: "destructive" }); }
    finally { setBusy(false); }
  };

  const authorize = () => {
    if (!isAddress(sessionKey)) return toast({ title: "Invalid session key", variant: "destructive" });
    return run("Session key authorized", async () => {
      const now = Math.floor(Date.now() / 1000);
      await write("authorizeSessionKey", [sessionKey, now, now + Number(hours) * 3600, parseEther(perCall || "0"), parseEther(daily || "0"), canSign]);
    });
  };

  const allowCall = () => {
    if (!isAddress(sessionKey) || !isAddress(target) || !/^0x[0-9a-fA-F]{8}$/.test(selector))
      return toast({ title: "Invalid permission", description: "Enter a session key, target, and four-byte selector.", variant: "destructive" });
    return run("Call permission added", () => write("setSessionPermission", [sessionKey, target, selector, true]));
  };

  const setTokenPolicy = () => {
    if (!isAddress(sessionKey) || !isAddress(target))
      return toast({ title: "Session key and ERC-20 token are required", variant: "destructive" });
    return run("Token spending policy saved", () => write("setSessionTokenPolicy", [
      sessionKey, target, parseEther(tokenPerCall || "0"), parseEther(tokenDaily || "0"), allowUnlimited,
    ]));
  };

  return <div className="space-y-4 rounded-md border p-4">
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div><h3 className="flex items-center gap-2 font-medium"><KeyRound className="h-4 w-4" />Agent session keys</h3><p className="text-xs text-muted-foreground">Keys expire automatically and become invalid when the Inception Token changes owner.</p></div>
      <div className="flex gap-2"><Button size="sm" variant={paused ? "default" : "destructive"} disabled={busy} onClick={() => run(paused ? "Vault unpaused" : "Vault paused", () => write("setPaused", [!paused]))}><PauseCircle className="mr-1 h-4 w-4" />{paused ? "Unpause" : "Emergency pause"}</Button><Button size="icon" variant="outline" onClick={() => refresh()}><RefreshCw className="h-4 w-4" /></Button></div>
    </div>
    {paused && <p className="rounded bg-destructive/10 p-2 text-sm text-destructive">Session and arbitrary-call execution is paused. Owner recovery remains available.</p>}
    {sessions.length > 0 && <div className="space-y-2">{sessions.map((item) => <div key={item.key} className="flex flex-wrap items-center justify-between gap-2 rounded border p-2 text-xs"><div><p className="break-all font-mono">{item.key}</p><p className="text-muted-foreground">{item.active ? "Active" : "Inactive"} · expires {new Date(item.validUntil * 1000).toLocaleString()} · {formatEther(item.perCall)} POL/call · {formatEther(item.daily)} POL/day{item.canSign ? " · signer" : ""}</p></div><Button size="sm" variant="outline" disabled={busy} onClick={() => run("Session key revoked", () => write("revokeSessionKey", [item.key]))}><Trash2 className="mr-1 h-3 w-3" />Revoke</Button></div>)}</div>}
    <div className="space-y-2"><Label>Session signer</Label><Input value={sessionKey} onChange={(event) => setSessionKey(event.target.value)} placeholder="0x…" /></div>
    <div className="grid gap-2 sm:grid-cols-3"><Input value={hours} onChange={(event) => setHours(event.target.value)} inputMode="numeric" placeholder="Hours" /><Input value={perCall} onChange={(event) => setPerCall(event.target.value)} placeholder="POL/call" /><Input value={daily} onChange={(event) => setDaily(event.target.value)} placeholder="POL/day" /></div>
    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={canSign} onChange={(event) => setCanSign(event.target.checked)} />Allow ERC-1271 identity signatures</label>
    <Button size="sm" onClick={authorize} disabled={busy}>Authorize key</Button>
    <div className="grid gap-2 sm:grid-cols-[1fr_8rem]"><Input value={target} onChange={(event) => setTarget(event.target.value)} placeholder="Allowed target / ERC-20 token" /><Input value={selector} onChange={(event) => setSelector(event.target.value)} placeholder="0x12345678" /></div>
    <div className="flex flex-wrap gap-2"><Button size="sm" variant="outline" onClick={allowCall} disabled={busy}>Allow contract call</Button></div>
    <div className="space-y-2 rounded border p-3"><p className="text-sm font-medium">ERC-20 spending policy for target</p><div className="grid gap-2 sm:grid-cols-2"><Input value={tokenPerCall} onChange={(event) => setTokenPerCall(event.target.value)} placeholder="Tokens/call (18 decimals)" /><Input value={tokenDaily} onChange={(event) => setTokenDaily(event.target.value)} placeholder="Tokens/day (18 decimals)" /></div><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={allowUnlimited} onChange={(event) => setAllowUnlimited(event.target.checked)} />Explicitly permit unlimited approvals</label><Button size="sm" variant="outline" onClick={setTokenPolicy} disabled={busy}>Save token policy</Button></div>
  </div>;
}
